import { GenerateReportDialog } from '@internal-audit/components/GenerateReportDialog';
import { PolicyMetricsSection } from '@internal-audit/components/PolicyMetricsSection';
import { PolicySummaryPanel } from '@internal-audit/components/PolicySummaryPanel';
import { useGetAuditReport } from '@internal-audit/hooks/useGetAuditReport';
import { Card, Container, Heading, Section, Text, Button, ButtonType, ButtonSize } from '@soteria/design-system-ve';
import { useMemo } from 'react';

import { DataList } from '../../../core/components';
import type { TableColumn } from '../../../core/components/DataList';
import * as styles from '../../pages/AuditReportPage.css';
import { AuditReportPageShimmer } from '../../pages/AuditReportPageShimmer';

// Label Performance DataList Component
interface LabelMetric {
  label: string;
  metrics: {
    precision: number;
    recall: number;
    f1Score: number;
    accuracy: number;
  };
}

interface LabelPerformanceDataListProps {
  labelMetrics: LabelMetric[];
}

function LabelPerformanceDataList({ labelMetrics }: LabelPerformanceDataListProps) {
  const columns: TableColumn<LabelMetric>[] = useMemo(
    () => [
      {
        key: 'label',
        header: 'Label',
        render: (item: LabelMetric) => <Text>{item.label}</Text>,
        width: '25%',
      },
      {
        key: 'precision',
        header: 'Precision',
        render: (item: LabelMetric) => <Text>{item.metrics.precision.toFixed(1)}%</Text>,
        width: '18.75%',
      },
      {
        key: 'recall',
        header: 'Recall',
        render: (item: LabelMetric) => <Text>{item.metrics.recall.toFixed(1)}%</Text>,
        width: '18.75%',
      },
      {
        key: 'f1Score',
        header: 'F1 Score',
        render: (item: LabelMetric) => <Text>{item.metrics.f1Score.toFixed(1)}%</Text>,
        width: '18.75%',
      },
      {
        key: 'accuracy',
        header: 'Accuracy',
        render: (item: LabelMetric) => <Text>{item.metrics.accuracy.toFixed(1)}%</Text>,
        width: '18.75%',
      },
    ],
    []
  );

  return (
    <div>
      <Heading level="h4">Label Performance Assessment</Heading>
      <div className={styles.labelPerformanceSection}>
        <DataList hideEmptyState columns={columns} data={labelMetrics} dataTypeName="label metric" emptyStateMessage="No label metrics available." />
      </div>
    </div>
  );
}

interface AuditReportsTabProps {
  communityId: string;
  sessionId: string;
}

// eslint-disable-next-line complexity
export function AuditReportsTab({ communityId, sessionId }: AuditReportsTabProps) {
  // Get audit report data
  const { reports, loading, errors } = useGetAuditReport(communityId, sessionId);

  // Handle loading state
  if (loading) {
    return <AuditReportPageShimmer />;
  }

  // Handle error state
  if (errors.length > 0) {
    return (
      <Container>
        <Section spacing="large">
          <Card className={styles.errorCard}>
            <Section spacing="medium">
              <Heading level="h2">Error Loading Report</Heading>
              <Text>{errors[0]?.userMessage || 'Failed to load audit report.'}</Text>
              <Text>Error Code: {errors[0]?.code || 'UNKNOWN'}</Text>
              <div className={styles.errorActions}>
                <Button buttonSize={ButtonSize.Default} buttonType={ButtonType.Tertiary} label="Retry" onClick={() => window.location.reload()} />
              </div>
            </Section>
          </Card>
        </Section>
      </Container>
    );
  }

  // Handle no reports found - show dialog instead of card
  if (reports.length === 0) {
    return (
      <>
        <div>
          <Text>No reports available for this audit session.</Text>
        </div>
        {/* Generate Report Dialog */}
        <GenerateReportDialog isOpen communityId={communityId || ''} sessionId={sessionId || ''} onClose={() => {}} />
      </>
    );
  }

  return (
    <div>
      {/* Audit Reports Content */}
      <Section spacing="large">
        <Heading level="h2">Audit Reports ({reports.length})</Heading>
        {reports.map(
          report =>
            report.metrics && (
              <div key={report.id}>
                <Section background="secondary" spacing="large">
                  <div className={styles.sectionContentWrapper}>
                    <Heading level="h4">Overall Performance Metrics</Heading>
                    <div className={styles.metricsGrid}>
                      <div className={styles.metricCard}>
                        <Text className={styles.metricLabel}>PRECISION</Text>
                        <Text className={`${styles.metricValue} ${styles.precisionValue}`}>{report.metrics.overall.precision.toFixed(1)}%</Text>
                      </div>
                      <div className={styles.metricCard}>
                        <Text className={styles.metricLabel}>RECALL</Text>
                        <Text className={`${styles.metricValue} ${styles.recallValue}`}>{report.metrics.overall.recall.toFixed(1)}%</Text>
                      </div>
                      <div className={styles.metricCard}>
                        <Text className={styles.metricLabel}>F1 SCORE</Text>
                        <Text className={`${styles.metricValue} ${styles.f1Value}`}>{report.metrics.overall.f1Score.toFixed(1)}%</Text>
                      </div>
                      <div className={styles.metricCard}>
                        <Text className={styles.metricLabel}>ACCURACY</Text>
                        <Text className={`${styles.metricValue} ${styles.accuracyValue}`}>{report.metrics.overall.accuracy.toFixed(1)}%</Text>
                      </div>
                    </div>
                  </div>
                </Section>

                {/* Grouped Label Sections */}
                {report.metrics.labelComparison && (
                  <Section background="primary" spacing="large">
                    <div className={styles.sectionContentWrapper}>
                      <Heading level="h4">Label Assessment Summary</Heading>
                      <div className={styles.comparisonGrid}>
                        <div className={styles.comparisonCard}>
                          <Text className={styles.comparisonLabel}>EXACT MATCHES</Text>
                          <Text className={`${styles.comparisonValue} ${styles.exactMatchValue}`}>
                            {report.metrics.labelComparison.exactMatchCount} ({report.metrics.labelComparison.exactMatchPercentage.toFixed(1)}%)
                          </Text>
                        </div>
                        <div className={styles.comparisonCard}>
                          <Text className={styles.comparisonLabel}>ADDITIONAL LABELS</Text>
                          <Text className={`${styles.comparisonValue} ${styles.additionalLabelsValue}`}>
                            {report.metrics.labelComparison.additionalLabelsCount} ({report.metrics.labelComparison.additionalLabelsPercentage.toFixed(1)}%)
                          </Text>
                        </div>
                        <div className={styles.comparisonCard}>
                          <Text className={styles.comparisonLabel}>MISSING LABELS</Text>
                          <Text className={`${styles.comparisonValue} ${styles.missingLabelsValue}`}>
                            {report.metrics.labelComparison.labelsMissingCount} ({report.metrics.labelComparison.labelsMissingPercentage.toFixed(1)}%)
                          </Text>
                        </div>
                        <div className={styles.comparisonCard}>
                          <Text className={styles.comparisonLabel}>BOTH MISSING & ADDITIONAL</Text>
                          <Text className={`${styles.comparisonValue} ${styles.bothMissingAdditionalValue}`}>
                            {report.metrics.labelComparison.bothMissingAndAdditionalCount} (
                            {report.metrics.labelComparison.bothMissingAndAdditionalPercentage.toFixed(1)}%)
                          </Text>
                        </div>
                      </div>
                    </div>
                  </Section>
                )}

                {report.metrics.byLabel && report.metrics.byLabel.length > 0 && (
                  <Section background="secondary" spacing="large">
                    <div className={styles.sectionContentWrapper}>
                      <LabelPerformanceDataList labelMetrics={report.metrics.byLabel} />
                    </div>
                  </Section>
                )}

                {/* Grouped Policy Sections */}
                <Section background="primary" spacing="large">
                  <div className={styles.sectionContentWrapper}>
                    {report.metrics.policySummary && report.metrics.policySummary.totalPoliciesAssessed > 0 ? (
                      <PolicySummaryPanel policySummary={report.metrics.policySummary} />
                    ) : (
                      <>
                        <Heading level="h4">Policy Assessment Summary</Heading>
                        <Text>
                          No policy summary data available. This section will display aggregate policy statistics when policy assessment data is provided.
                        </Text>
                      </>
                    )}
                  </div>
                </Section>

                <Section background="secondary" spacing="large">
                  <div className={styles.sectionContentWrapper}>
                    {report.metrics.byPolicy && report.metrics.byPolicy.length > 0 ? (
                      <PolicyMetricsSection policyMetrics={report.metrics.byPolicy} />
                    ) : (
                      <>
                        <Heading level="h4">Policy Performance Assessment</Heading>
                        <Text>
                          No individual policy metrics available. This section will display detailed policy performance data when policy assessment data is
                          provided.
                        </Text>
                      </>
                    )}
                  </div>
                </Section>
              </div>
            )
        )}

        {/* Footer with created by information */}
        {reports.length > 0 && (
          <Section spacing="large">
            <div className={styles.reportFooter}>
              <Text className={styles.footerText}>
                Created {new Date(reports[0].createdAt).toLocaleDateString('en-CA')} by {reports[0].createdBy}
              </Text>
            </div>
          </Section>
        )}
      </Section>
    </div>
  );
}
